import React, { useEffect, useState } from 'react';
import {
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  TextArea,
} from '@razorpay/blade/components';
import pluralize from 'pluralize';
import { FlexiReimbursementClaim } from 'api/gql-utils/flexibleBenefits/types';
import { DecisionType } from './types';

type BulkActionModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  actionType: DecisionType;
  selectedClaims: FlexiReimbursementClaim[];
  onConfirm: (data: {
    claims: Array<{
      claimId: string;
      approvedAmount?: number;
      reviewComments?: string;
    }>;
    commonComment: string;
  }) => void;
  isLoading?: boolean;
};

export const BulkActionModal = ({
  isOpen,
  onDismiss,
  actionType,
  selectedClaims,
  onConfirm,
  isLoading = false,
}: BulkActionModalProps) => {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (isOpen) {
      setComment('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    const claims = selectedClaims.map((claim) => ({
      claimId: claim.claim_id,
      approvedAmount: actionType === 'approve' ? claim.claim_amount : undefined,
      reviewComments: comment ?? '',
    }));

    onConfirm({ claims, commonComment: comment });
  };

  const isApprove = actionType === 'approve';
  const claimCount = selectedClaims.length;
  const title = isApprove
    ? `Approve ${claimCount} reimbursement ${pluralize('claim', claimCount)}?`
    : `Reject ${claimCount} reimbursement ${pluralize('claim', claimCount)}?`;

  const isConfirmDisabled =
    isLoading || (actionType === 'reject' && !comment.trim()) || selectedClaims.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="small"
      accessibilityLabel={`${actionType} reimbursement claims modal`}>
      <ModalHeader title={title} />
      <ModalBody>
        <TextArea
          label="Comment"
          placeholder={
            isApprove
              ? 'Add context on approval of this invoice'
              : 'Please mention the exact reason of rejection.'
          }
          name="comment"
          value={comment}
          onChange={({ value }) => setComment(value ?? '')}
          numberOfLines={3}
          necessityIndicator={isApprove ? 'optional' : 'required'}
        />
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.5" width="100%">
          <Button isFullWidth variant="tertiary" onClick={onDismiss} isDisabled={isLoading}>
            Back
          </Button>
          <Button
            isFullWidth
            onClick={handleConfirm}
            isDisabled={isConfirmDisabled}
            isLoading={isLoading}>
            Confirm
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
